import AnalyticsEventNames from './AnalyticsEventNames';

const notImplementedError = new Error('Analytics method not implemented');

export default class Analytics {
  constructor(currencyStore, accountStore, uiStore) {
    this.currencyStore = currencyStore;
    this.accountStore = accountStore;
    this.uiStore = uiStore;
  }

  /**
   * Dispatch analytics events for integrations to use.
   *
   * @param {string} name
   * @param {Object} payload
   */
  dispatchEvent(name, payload) {
    const detail = {
      detail: {
        name,
        payload: {
          currencyCode: this.currencyStore.currencyCode,
          ...payload
        }
      }
    };
    // Dispatch a specific event for integrations that only need specific events.
    const event = new CustomEvent(`analytics:${name}`, detail);
    document.dispatchEvent(event);
    // Dispatch a catch-all event for integrations that use all events.
    const analyticsEvent = new CustomEvent('analytics', detail);
    document.dispatchEvent(analyticsEvent);
  }

  /**
   * @param {string} pageName
   */
  setPage(pageName) {
    this.dispatchEvent(AnalyticsEventNames.setPage, {
      pageName
    });
  }

  /**
   * @param {string} listName
   * @param {Array} productList An array with objects in the format { position, product }.
   */
  productClick(listName, productList) {
    this.dispatchEvent(AnalyticsEventNames.productClick, {
      listName,
      productList
    });
  }

  /**
   * @param {string} listName
   * @param {Array} productList An array with objects in the format { position, product }.
   */
  productImpressions(listName, productList) {
    this.dispatchEvent(AnalyticsEventNames.productImpressions, {
      listName,
      productList,
      pageName: this.uiStore.currentPage
    });
  }

  /**
   * @param {Array} productList An array with objects in the format { product }.
   */
  productDetail(productList) {
    this.dispatchEvent(AnalyticsEventNames.productDetail, {
      productList
    });
  }

  /**
   * @param {Array} productList An array with objects in the format { product, quantity, activeProductId, price }.
   */
  addToCart(productList) {
    this.dispatchEvent(AnalyticsEventNames.addToCart, {
      productList
    });
  }

  /**
   * @param {Array} productList An array with objects in the format { product, quantity, activeProductId, price }.
   */
  removeFromCart(productList) {
    this.dispatchEvent(AnalyticsEventNames.removeFromCart, {
      productList
    });
  }

  /**
   * Send all products from the cart.
   */
  cartStatus(cart) {
    this.dispatchEvent(AnalyticsEventNames.cartStatus, {
      cart
    });
  }

  /**
   * Clear all products from the cart.
   */
  clearCart() {
    this.dispatchEvent(AnalyticsEventNames.clearCart, {});
  }

  /**
   * @param {Array} bannerList An array with objects in the format { bannerZone, banner }.
   */
  promoView(bannerList) {
    this.dispatchEvent(AnalyticsEventNames.promoView, {
      bannerList
    });
  }

  /**
   * @param {Array} bannerList An array with objects in the format { bannerZone, banner }.
   */
  promotionClick(bannerList) {
    this.dispatchEvent(AnalyticsEventNames.promotionClick, {
      bannerList
    });
  }

  /**
   * @param searchText
   * @param resultCounts
   */
  quickSearch(searchText, resultCounts) {
    this.dispatchEvent(AnalyticsEventNames.quickSearch, {
      searchText,
      resultCounts
    });
  }

  /**
   * When quick search suggestions are selected, the information is sent through this function.
   * Can be used to train suggestions.
   *
   * @param searchText
   * @param suggestionType
   * @param suggestionValue
   */
  suggestionSelection(searchText, suggestionType, suggestionValue) {
    this.dispatchEvent(AnalyticsEventNames.suggestionSelection, {
      searchText,
      suggestionType,
      suggestionValue
    });
  }

  checkout(productList, actionField) {
    this.dispatchEvent(AnalyticsEventNames.checkout, {
      productList,
      actionField
    });
  }

  checkoutOption() {
    throw notImplementedError;
  }

  /**
   * @param {Order} order The entire order object.
   * @param {Product[]} products Real product objects representing the products included in the order.
   */
  purchase(order, products) {
    this.dispatchEvent(AnalyticsEventNames.purchase, {
      order,
      products
    });
  }

  refund() {
    throw notImplementedError;
  }

  updateDimension(dimensionKey, dimensionValue) {
    this.dispatchEvent(AnalyticsEventNames.updateDimension, {
      dimensionKey,
      dimensionValue
    });
  }

  reserveAndCollect(data) {
    this.dispatchEvent(AnalyticsEventNames.reserveAndCollect, data);
  }

  pageNotFound(payload) {
    this.dispatchEvent(AnalyticsEventNames.pageNotFound, payload);
  }
}



// WEBPACK FOOTER //
// ./src/analytics/Analytics.js